import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, Wand2, Printer, Trash2, X } from 'lucide-react';
import BackLink from '../components/BackLink';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';
import { supportsCapability } from '../lib/capabilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SeatingPage() {
  const { auraId } = useParams();
  const [aura, setAura] = useState(null);
  const [tables, setTables] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // table object
  const [dragOverId, setDragOverId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, t, r] = await Promise.all([
        axios.get(`${API_URL}/api/auras/${auraId}`),
        axios.get(`${API_URL}/api/auras/${auraId}/tables`),
        axios.get(`${API_URL}/api/auras/${auraId}/rsvps`),
      ]);
      setAura(a.data);
      setTables(t.data || []);
      setRsvps((r.data?.rsvps || []).filter(x => x.attending === 'yes'));
    } catch (e) {
      setErr('Failed to load seating data.');
    } finally { setLoading(false); }
  }, [auraId]);

  useEffect(() => { load(); }, [load]);

  const seatedIds = new Set(tables.flatMap(t => t.seats.map(s => s.rsvp_id)));
  const unseated = rsvps.filter(r => !seatedIds.has(r.id));

  const addTable = async () => {
    const n = prompt('Table name?', `Table ${tables.length + 1}`);
    if (!n) return;
    const cap = parseInt(prompt('Capacity?', '8') || '8', 10);
    try {
      await axios.post(`${API_URL}/api/auras/${auraId}/tables`, { name: n, capacity: cap });
      load();
    } catch (e) { alert('Failed to create table'); }
  };

  const updateTable = async (id, patch) => {
    try {
      await axios.put(`${API_URL}/api/tables/${id}`, patch);
      load();
    } catch (e) { alert('Failed to update table'); }
  };

  const deleteTable = async (id) => {
    if (!window.confirm('Delete this table? Seats will be cleared.')) return;
    try {
      await axios.delete(`${API_URL}/api/tables/${id}`);
      setEditing(null);
      load();
    } catch (e) { alert('Failed to delete table'); }
  };

  const assignRsvp = async (tableId, rsvpId) => {
    try {
      await axios.post(`${API_URL}/api/tables/${tableId}/seat`, { rsvp_id: rsvpId, seat_order: 0 });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to assign'); }
  };

  const unassign = async (tableId, rsvpId) => {
    try {
      await axios.delete(`${API_URL}/api/tables/${tableId}/seat/${rsvpId}`);
      load();
    } catch (e) { alert('Failed to unassign'); }
  };

  const autoSuggest = async (strategy) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auras/${auraId}/seating/auto-suggest`, { strategy });
      for (const t of data.tables) {
        for (const rid of t.rsvp_ids) {
          try { await axios.post(`${API_URL}/api/tables/${t.table_id}/seat`, { rsvp_id: rid, seat_order: 0 }); } catch (_) {}
        }
      }
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Auto-suggest failed'); }
  };

  const printPDF = () => {
    window.open(`${API_URL}/api/auras/${auraId}/seating.pdf`, '_blank');
  };

  const onDragStart = (e, rsvpId, fromTableId = null) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ rsvpId, fromTableId }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = async (e, tableId) => {
    e.preventDefault();
    setDragOverId(null);
    try {
      const { rsvpId } = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (!rsvpId) return;
      await assignRsvp(tableId, rsvpId);
    } catch (_) {}
  };

  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center text-champagne">Loading…</div>;

  // Seating charts only apply to occasions that support them (e.g. weddings).
  // Guards direct navigation to /aura/:id/seating for other event types.
  if (aura && !supportsCapability(aura, 'seating')) {
    return (
      <div className="min-h-screen bg-midnight text-text-light">
        <SiteHeader />
        <div className="max-w-2xl mx-auto p-6">
          <div className="mb-4"><BackLink to={`/aura/${auraId}`} label="Back to event" /></div>
          <div className="bg-white/[0.03] border border-white/10 rounded-xl p-8 text-center">
            <h1 className="font-serif text-2xl text-champagne mb-2">Seating isn’t available for this event</h1>
            <p className="text-text-muted text-sm">Seating charts are designed for weddings. This event type doesn’t use them.</p>
          </div>
        </div>
        <FooterExpanded />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight text-text-light">
      <SiteHeader />
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="mb-4"><BackLink to={`/aura/${auraId}`} label="Back to event" /></div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-serif text-3xl text-champagne">Seating chart{aura ? ` — ${aura.title}` : ''}</h1>
          <div className="flex flex-wrap gap-2">
            <button onClick={addTable} className="px-3 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> Add Table</button>
            <button onClick={() => autoSuggest('random')} className="px-3 py-2 rounded-lg border border-white/20 text-sm flex items-center gap-2"><Wand2 className="w-4 h-4" /> Auto-suggest</button>
            <button onClick={printPDF} className="px-3 py-2 rounded-lg border border-white/20 text-sm flex items-center gap-2"><Printer className="w-4 h-4" /> Print PDF</button>
          </div>
        </div>
        {err && <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/30 text-red-200 text-sm">{err}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Unseated list */}
          <div className="lg:col-span-1">
            <h2 className="font-serif text-xl text-champagne mb-3">Unseated ({unseated.length})</h2>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-3 space-y-2 max-h-[70vh] overflow-y-auto">
              {unseated.length === 0 && <div className="text-text-muted text-sm p-2">All guests seated.</div>}
              {unseated.map(r => (
                <div
                  key={r.id}
                  draggable
                  onDragStart={(e) => onDragStart(e, r.id)}
                  className="px-3 py-2 rounded-lg bg-midnight border border-white/10 text-sm cursor-grab hover:border-champagne/40"
                  data-testid={`unseated-${r.id}`}
                >
                  {r.guest_name}
                  {r.party_size > 1 && <span className="text-text-muted text-xs ml-2">+{r.party_size - 1}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Tables grid */}
          <div className="lg:col-span-2">
            <h2 className="font-serif text-xl text-champagne mb-3">Tables ({tables.length})</h2>
            {tables.length === 0 && (
              <div className="text-text-muted text-sm p-6 border border-dashed border-white/10 rounded-xl text-center">
                No tables yet. Click "Add Table" to start.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tables.map(t => {
                const isOver = dragOverId === t.id;
                const full = t.seats.length >= t.capacity;
                return (
                  <div
                    key={t.id}
                    onDragOver={(e) => { e.preventDefault(); setDragOverId(t.id); }}
                    onDragLeave={() => setDragOverId(null)}
                    onDrop={(e) => onDrop(e, t.id)}
                    className={`rounded-xl border p-3 transition-colors ${isOver ? 'border-champagne bg-champagne/5' : 'border-white/10 bg-white/[0.03]'}`}
                    data-testid={`table-${t.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={() => setEditing(t)} className="text-left">
                        <div className="font-serif text-lg text-champagne hover:underline">{t.name}</div>
                        <div className="text-xs text-text-muted">{t.seats.length}/{t.capacity} {full && '· full'}</div>
                      </button>
                    </div>
                    <div className="space-y-1">
                      {t.seats.map(s => {
                        const r = rsvps.find(x => x.id === s.rsvp_id);
                        return (
                          <div
                            key={s.id}
                            draggable
                            onDragStart={(e) => onDragStart(e, s.rsvp_id, t.id)}
                            className="flex items-center justify-between px-2 py-1 rounded bg-midnight border border-white/10 text-sm cursor-grab"
                          >
                            <span>{r?.guest_name || `Guest #${s.rsvp_id}`}</span>
                            <button onClick={() => unassign(t.id, s.rsvp_id)} className="text-text-muted hover:text-red-400" aria-label="Remove">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                      {t.seats.length === 0 && <div className="text-xs text-text-muted px-2">Drop guests here.</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {editing && (
          <TableEditModal
            table={editing}
            onClose={() => setEditing(null)}
            onSave={(patch) => { updateTable(editing.id, patch); setEditing(null); }}
            onDelete={() => deleteTable(editing.id)}
          />
        )}
      </div>
      <FooterExpanded />
    </div>
  );
}

function TableEditModal({ table, onClose, onSave, onDelete }) {
  const [name, setName] = useState(table.name);
  const [capacity, setCapacity] = useState(table.capacity);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-midnight border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-champagne">Edit table</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-text-muted" /></button>
        </div>
        <label className="text-xs text-text-muted">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mb-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
        <label className="text-xs text-text-muted">Capacity</label>
        <input type="number" min="1" value={capacity} onChange={(e) => setCapacity(parseInt(e.target.value, 10) || 1)} className="w-full mb-4 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
        <div className="flex justify-between">
          <button onClick={onDelete} className="px-3 py-2 rounded-lg border border-red-500/30 text-red-300 text-sm flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-white/20 text-sm">Cancel</button>
            <button onClick={() => onSave({ name, capacity })} className="px-3 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
